import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import FlavorService from '../../services/FlavorService';
import Swal from 'sweetalert2';

const FlavorCategoryModal = ({ show, onHide, category, onSuccess }) => {
    const [name, setName] = useState('');
    const [displayOrder, setDisplayOrder] = useState(0);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (category) {
            setName(category.name);
            setDisplayOrder(category.displayOrder);
        } else {
            setName('');
            setDisplayOrder(0);
        }
        setErrors({});
    }, [category, show]);

    const validateForm = () => {
        const newErrors = {};
        if (!name.trim()) {
            newErrors.name = 'El nombre es obligatorio';
        } else if (name.length > 100) {
            newErrors.name = 'El nombre no puede tener más de 100 caracteres';
        }

        if (displayOrder < 0) {
            newErrors.displayOrder = 'El orden debe ser un número positivo';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const categoryData = {
                name,
                displayOrder: parseInt(displayOrder),
                status: true
            };

            if (category) {
                await FlavorService.updateFlavorCategory(category.id, {
                    ...categoryData,
                    id: category.id
                });
            } else {
                await FlavorService.createFlavorCategory(categoryData);
            }

            Swal.fire({
                icon: 'success',
                title: 'Éxito!',
                text: 'Categoría ' + (category ? 'actualizada' : 'creada') + ' correctamente',
                timer: 1500,
                showConfirmButton: false,
            });

            onSuccess();
            onHide();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.errorMsg || 'Error al ' + (category ? 'actualizar' : 'crear') + ' la categoría',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {category ? 'Editar' : 'Nueva'} Categoría de Gustos
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Nombre</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ingrese el nombre de la categoría"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Orden de visualización</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={displayOrder}
                            onChange={(e) => setDisplayOrder(e.target.value)}
                            isInvalid={!!errors.displayOrder}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.displayOrder}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            Número que determina el orden de visualización de la categoría
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Guardando...' : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

FlavorCategoryModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    category: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        displayOrder: PropTypes.number,
        status: PropTypes.bool
    }),
    onSuccess: PropTypes.func.isRequired
};

export default FlavorCategoryModal;